import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
	NAV_SETTINGS_PATH,
	SETTINGS_NAV_ITEM,
	entryPath,
	hasChildMenu,
	isChildActive,
	isPathActive,
	navSections,
	visibleChildren,
	visibleNavItems,
} from '../../lib/nav-items'
import type { NavItem } from '../../lib/nav-items'
import { usePrefetchIntent } from '../../hooks/usePrefetchIntent'
import { AccountMenu } from './AccountMenu'

interface MobileDrawerNavProps {
	isOpen: boolean
	onNavigate: () => void
}

const ROW_BASE =
	'flex items-center gap-2.5 px-3 py-3 rounded-lg border-none text-sm cursor-pointer text-left w-full min-h-[44px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome-foreground/80 focus-visible:ring-offset-1 focus-visible:ring-offset-chrome'

const CHILD_BASE =
	'flex items-center gap-2 px-2.5 py-2.5 rounded-lg border-none text-[13px] cursor-pointer text-left w-full min-h-[40px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome-foreground/80'

function rowClass(isActive: boolean): string {
	return `${ROW_BASE} ${
		isActive
			? 'bg-chrome-foreground text-chrome font-medium'
			: 'bg-transparent text-chrome-foreground/80 hover:text-chrome-foreground hover:bg-chrome-foreground/8'
	}`
}

function childRowClass(isActive: boolean): string {
	return `${CHILD_BASE} ${
		isActive
			? 'bg-chrome-foreground text-chrome font-medium'
			: 'bg-transparent text-chrome-foreground/80 hover:text-chrome-foreground hover:bg-chrome-foreground/8'
	}`
}

export function MobileDrawerNav({ isOpen, onNavigate }: MobileDrawerNavProps) {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const { hasPermission, isLoading: isAuthLoading } = useAuth()
	const intent = usePrefetchIntent()

	const isSettingsVisible = hasPermission(SETTINGS_NAV_ITEM.permission!)

	function sectionForPath(path: string): string | null {
		const parent = visibleNavItems(hasPermission).find((item) => hasChildMenu(item, hasPermission) && isPathActive(path, item.path))
		return parent ? parent.path : null
	}

	const [expandedPath, setExpandedPath] = useState<string | null>(() => sectionForPath(pathname))
	const [isPreviouslyOpen, setWasOpen] = useState(isOpen)

	if (isOpen !== isPreviouslyOpen) {
		setWasOpen(isOpen)
		if (isOpen) setExpandedPath(sectionForPath(pathname))
	}

	function navigateAndClose(path: string) {
		navigate(path)
		onNavigate()
	}

	function toggle(path: string) {
		setExpandedPath((current) => (current === path ? null : path))
	}

	if (isAuthLoading) {
		return (
			<nav className="flex flex-col gap-1" aria-label="Main navigation">
				{Array.from({ length: 5 }).map((_, i) => (
					<div key={i} className="h-11 rounded-lg bg-chrome-foreground/10 animate-pulse" />
				))}
			</nav>
		)
	}

	function renderItem(item: NavItem) {
		const isSectionActive = isPathActive(pathname, item.path)

		if (!hasChildMenu(item, hasPermission)) {
			return (
				<button
					key={item.path}
					{...intent(entryPath(item, hasPermission))}
					onClick={() => navigateAndClose(entryPath(item, hasPermission))}
					aria-current={isSectionActive ? 'page' : undefined}
					className={rowClass(isSectionActive)}
				>
					<i className={`ti ${item.icon} text-lg`} aria-hidden="true" />
					{item.label}
				</button>
			)
		}

		const isExpanded = expandedPath === item.path

		return (
			<div key={item.path}>
				<button
					onClick={() => toggle(item.path)}
					aria-expanded={isExpanded}
					className={`${rowClass(isSectionActive && !isExpanded)} justify-between`}
				>
					<span className="flex items-center gap-2.5">
						<i className={`ti ${item.icon} text-lg`} aria-hidden="true" />
						{item.label}
					</span>
					<i
						className={`ti ti-chevron-down text-base transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
						aria-hidden="true"
					/>
				</button>

				{isExpanded && (
					<div className="ml-3 mt-0.5 mb-1 flex flex-col gap-0.5 border-l border-chrome-foreground/15 pl-3">
						{visibleChildren(item, hasPermission).map((child) => {
							const isActive = isChildActive(pathname, item, child)
							return (
								<button
									key={child.path}
									{...intent(child.path)}
									onClick={() => navigateAndClose(child.path)}
									aria-current={isActive ? 'page' : undefined}
									className={childRowClass(isActive)}
								>
									<i className={`ti ${child.icon} text-base`} aria-hidden="true" />
									{child.label}
								</button>
							)
						})}
					</div>
				)}
			</div>
		)
	}

	return (
		<nav className="flex flex-col" aria-label="Main navigation">
			{navSections(hasPermission).map((section) => (
				<div key={section.label ?? 'main'} className="flex flex-col gap-0.5">
					{section.label && (
						<h2 className="px-3 pt-4 pb-1 text-[11px] font-medium uppercase tracking-wider text-chrome-foreground/80">{section.label}</h2>
					)}
					{section.items.map(renderItem)}
				</div>
			))}

			<button
				onClick={() => navigateAndClose(NAV_SETTINGS_PATH)}
				aria-current={pathname === NAV_SETTINGS_PATH ? 'page' : undefined}
				className={`${rowClass(pathname === NAV_SETTINGS_PATH)} mt-3`}
			>
				<i className="ti ti-adjustments-horizontal text-lg" aria-hidden="true" />
				Customize navigation
			</button>

			{isSettingsVisible && (
				<button
					onClick={() => navigateAndClose(SETTINGS_NAV_ITEM.path)}
					aria-current={isPathActive(pathname, SETTINGS_NAV_ITEM.path) ? 'page' : undefined}
					className={`${rowClass(isPathActive(pathname, SETTINGS_NAV_ITEM.path))} mt-3 pt-3 rounded-t-none border-t border-chrome-foreground/10`}
				>
					<i className={`ti ${SETTINGS_NAV_ITEM.icon} text-lg`} aria-hidden="true" />
					{SETTINGS_NAV_ITEM.label}
				</button>
			)}

			<AccountMenu onNavigate={onNavigate} />
		</nav>
	)
}
